import React, { useState, useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import './ProfileTooltip.css';
import resumePdf from '../assets/Alvin_Ahmad.pdf';
import AnimatedButton from './AnimatedButton';
import { fetchResumeLinks } from '../services/supabaseService';
import { useLanguage } from '../context/LanguageContext';

const ProfileTooltip = ({ children, isDarkTheme }) => {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [resumeUrls, setResumeUrls] = useState({
    download_url: resumePdf,
    preview_url: 'https://canva.link/ud62pei3nhx4sfh'
  });
  const tooltipRef = useRef(null);
  const containerRef = useRef(null);
  const hoverTimeoutRef = useRef(null);

  useEffect(() => {
    // Determine active role from URL or localStorage
    const params = new URLSearchParams(window.location.search);
    let activeRole = params.get('role');
    
    if (activeRole) {
      localStorage.setItem('portfolio_active_role', activeRole);
    } else {
      activeRole = localStorage.getItem('portfolio_active_role') || 'software_developer';
    }

    // Fetch links from Supabase
    const loadResumeLinks = async () => {
      const data = await fetchResumeLinks(activeRole);
      if (data && data.download_url && data.preview_url) {
        setResumeUrls({
          download_url: data.download_url,
          preview_url: data.preview_url
        });
      }
    };

    loadResumeLinks();
  }, []);

  useEffect(() => {
    const tooltip = tooltipRef.current;
    if (!tooltip) return;

    if (isOpen) {
      gsap.set(tooltip, { scale: 0, opacity: 0, transformOrigin: 'top left' });
      gsap.to(tooltip, {
        scale: 1,
        opacity: 1,
        duration: 0.4,
        ease: 'elastic.out(1, 0.5)',
        delay: 0.05
      });
    } else {
      gsap.to(tooltip, {
        scale: 0,
        opacity: 0,
        duration: 0.2,
        ease: 'power2.in'
      });
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setIsHovering(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMouseEnter = () => {
    setIsHovering(true);
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    // Small delay before showing to prevent accidental triggers
    hoverTimeoutRef.current = setTimeout(() => {
      setIsOpen(true);
    }, 200);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    // Delay hiding to allow moving to tooltip
    hoverTimeoutRef.current = setTimeout(() => {
      if (!isHovering) {
        setIsOpen(false);
      }
    }, 300);
  };

  const handleTooltipMouseEnter = () => {
    setIsHovering(true);
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
  };

  const handleTooltipMouseLeave = () => {
    setIsHovering(false);
    hoverTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 200);
  };

  const handleClick = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div
      ref={containerRef}
      className="profile-tooltip-container"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
    >
      {/* Profile Image (Trigger) */}
      {children}

      {/* Tooltip Popout */}
      <div
        ref={tooltipRef}
        className={`profile-tooltip ${isDarkTheme ? 'dark' : 'light'}`}
        style={{ opacity: 0, scale: 0 }}
        onMouseEnter={handleTooltipMouseEnter}
        onMouseLeave={handleTooltipMouseLeave}
      >
        <div className="profile-tooltip-inner">

          {/* Title */}
          <h4 className="profile-tooltip-title">{t('profile.hireMe')}</h4>

          {/* Description */}
          <p className="profile-tooltip-desc" style={{ marginTop: '16px' }}>
            {t('profile.resumeDesc')}
          </p>

          {/* Links */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', width: '100%' }}>
            <AnimatedButton
              onClick={() => {
                const link = document.createElement('a');
                link.href = resumeUrls.download_url;
                // Only add download attribute if it's a relative/local PDF, not an external URL like GDrive
                if (resumeUrls.download_url.endsWith('.pdf') && !resumeUrls.download_url.startsWith('http')) {
                  link.download = "Alvin_Ahmad_Resume.pdf";
                } else {
                  link.target = "_blank";
                }
                link.click();
              }}
              style={{
                backgroundColor: 'var(--v2-color-charcoal)',
                color: 'var(--v2-color-white)',
                borderRadius: '12px',
                padding: '12px 24px',
                fontWeight: 800,
                fontSize: '1rem',
                fontFamily: 'var(--v2-font-body)',
                width: '100%',
                boxSizing: 'border-box',
                border: 'none',
                cursor: 'pointer'
              }}
              bgColor="transparent"
              textColor="inherit"
              hoverBgColor="#673df2"
              hoverTextColor="#ffffff"
            >
              {t('profile.getResume')}
            </AnimatedButton>

            <a href={resumeUrls.preview_url} target="_blank" rel="noopener noreferrer" className="profile-tooltip-link">
              <span className="profile-tooltip-link-text">{t('profile.previewResume')}</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="100%" viewBox="0 0 169 10" fill="none" className="profile-tooltip-link-svg">
                <path d="M1 6.5661C56.3941 3.06082 112.187 1.20095 168 0.999878" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
                <path d="M32.1313 8.63371C68.2147 6.92799 104.462 6.13378 140.695 6.25107" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileTooltip;
